import { IconBoxMultiple1 } from '@tabler/icons-react'
import { Rule } from 'antd/lib/form'

import { RadioCaptureBlock as RenderRadioCaptureBlock } from '../../templateRenderer/CaptureBlocks/RadioCaptureBlock'
import { DisplayBlock, RadioCaptureBlock } from '../../types/blueprint'

import {
  BaseBlockConfig,
  Field,
  getBlockDesiredState,
  RadioField,
  SelectField,
  TypeOption,
} from './_BaseBlockConfig'

import { Pill } from '@/components/shared-components/BUI/Pill'
import { Flexbox } from '@/components/shared-components/Flexbox'

class Config extends BaseBlockConfig<RadioCaptureBlock> {
  public override kind = 'capture' as const
  public override type = 'radio' as const

  public override enableAdminOnlyVisiblity = true
  public override enableBlockToCsvColumn = true
  public override enableBlockToColumnDisplay = true

  public override blockTypeConfig: TypeOption = {
    kind: this.kind,
    value: this.type,
    label: 'Single select',
    icon: IconBoxMultiple1,
    helpText: 'Choose a single value from multiple options',
  }

  public override blockFieldsConfig: Field[] = [
    {
      name: 'radioButtons',
      label: 'Radio buttons',
      field: RadioField,
      required: true,
    },
    {
      name: 'optionType',
      label: 'Display type',
      field: SelectField,
      options: [
        { value: 'default', label: 'Radios' },
        { value: 'button', label: 'Buttons', default: true },
      ],
    },
  ]

  public override mapFormValueToBlock(
    block: RadioCaptureBlock,
    value: unknown
  ): RadioCaptureBlock {
    return {
      ...block,
      radioButtons: block?.radioButtons?.map((option) => ({
        ...option,
        checked: option.value === value,
      })),
    }
  }

  public override mapBlockToDisplayBlock(
    block: RadioCaptureBlock
  ): DisplayBlock {
    const { radioButtons, ...restOfBlock } = block

    return {
      ...restOfBlock,
      checklist: radioButtons?.filter((r) => r.checked),
      type: 'checklist',
      kind: 'display',
    }
  }

  public override mapBlockToCsvValue(block: RadioCaptureBlock): string | null {
    return block.radioButtons
      ?.filter((o) => o.checked)
      .map((option) => option.label)
      .join(', ')
  }

  public override mapBlockToColumnDisplay(
    block: RadioCaptureBlock
  ): React.ReactNode {
    const selectedOption = block.radioButtons?.filter((o) => o.checked)

    return (
      <Flexbox gap="0.5rem" alignItems="center" flexWrap="wrap">
        {selectedOption?.map((option) => (
          <Pill
            key={option.label}
            whiteBg
            size="small"
            label={option.label?.length ? option.label : block.name}
            {...getBlockDesiredState(option)}
          />
        ))}
      </Flexbox>
    )
  }

  public override renderBlock(
    block: RadioCaptureBlock,
    rules?: Rule[]
  ): React.ReactNode {
    return <RenderRadioCaptureBlock block={block} rules={rules} />
  }
}

export const RadioCaptureBlockConfig = new Config()
